import React from 'react';
import { usePermissions } from '@/hooks/usePermissions';
import { Permission, Role } from '@/types/permissions';
import { AlertTriangle, Lock } from 'lucide-react';
import { useTranslation } from 'react-i18next';

interface ProtectedComponentProps {
  children: React.ReactNode;
  // 权限检查选项（至少需要其中一个）
  permission?: Permission;
  permissions?: Permission[];
  role?: Role;
  roles?: Role[];
  minimumRole?: Role;
  // 权限检查逻辑
  requireAll?: boolean; // true: 需要所有权限，false: 需要任意权限（默认）
  // 无权限时的展示
  fallback?: React.ReactNode;
  showFallback?: boolean; // 是否显示无权限提示，默认true
  className?: string;
}

/**
 * 权限保护组件
 * 根据用户权限条件性渲染子组件
 */
export const ProtectedComponent: React.FC<ProtectedComponentProps> = ({
  children,
  permission,
  permissions = [],
  role,
  roles = [],
  minimumRole,
  requireAll = false,
  fallback,
  showFallback = true,
  className = ""
}) => {
  const { t } = useTranslation();
  const {
    hasAnyPermission,
    hasAllPermissions,
    hasAnyRole,
    hasMinimumRole,
    user
  } = usePermissions();

  // 如果用户未登录，直接返回无权限状态
  if (!user) {
    return showFallback ? (
      <UnauthorizedFallback 
        message={t('errors.loginRequired')}
        fallback={fallback}
        className={className}
      />
    ) : null;
  }

  // 构建所有需要检查的权限
  const allPermissions = [
    ...(permission ? [permission] : []),
    ...permissions
  ];

  // 构建所有需要检查的角色
  const allRoles = [
    ...(role ? [role] : []),
    ...roles
  ];

  // 权限检查逻辑
  let hasRequiredPermission = true;

  // 检查权限
  if (allPermissions.length > 0) {
    if (requireAll) {
      hasRequiredPermission = hasAllPermissions(allPermissions);
    } else {
      hasRequiredPermission = hasAnyPermission(allPermissions);
    }
  }

  // 检查角色
  if (hasRequiredPermission && allRoles.length > 0) {
    hasRequiredPermission = hasAnyRole(allRoles);
  }

  // 检查最小角色要求
  if (hasRequiredPermission && minimumRole) {
    hasRequiredPermission = hasMinimumRole(minimumRole);
  }

  // 如果没有权限
  if (!hasRequiredPermission) {
    return showFallback ? (
      <UnauthorizedFallback 
        message={t('auth.permissionDenied')}
        fallback={fallback}
        className={className}
      />
    ) : null;
  }

  // 有权限，渲染子组件
  return <>{children}</>;
};

/**
 * 默认的无权限提示组件
 */
const UnauthorizedFallback: React.FC<{
  message: string;
  fallback?: React.ReactNode;
  className?: string;
}> = ({ message, fallback, className = "" }) => {
  const { t } = useTranslation();
  if (fallback) {
    return <>{fallback}</>;
  }

  return (
    <div className={`flex items-center justify-center py-8 ${className}`}>
      <div className="text-center">
        <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-red-100">
          <Lock className="h-6 w-6 text-red-600" />
        </div>
        <h3 className="mt-4 text-sm font-medium text-gray-900">
          {message}
        </h3>
        <p className="mt-2 text-sm text-gray-500">
          {t('errors.noPermissionToViewContent')}
        </p>
      </div>
    </div>
  );
};

/**
 * 敏感信息保护组件
 * 专门用于隐藏财务等敏感数据
 */
export const SensitiveDataProtection: React.FC<{
  children: React.ReactNode;
  level?: 'financial' | 'management' | 'system';
  placeholder?: React.ReactNode;
  className?: string;
}> = ({ 
  children, 
  level = 'financial',
  placeholder,
  className = ""
}) => {
  const { t } = useTranslation();
  const { canViewFinancials, hasPermission } = usePermissions();

  let hasAccess = false;

  switch (level) {
    case 'financial':
      hasAccess = canViewFinancials();
      break;
    case 'management':
      hasAccess = hasPermission(Permission.MANAGE_STAFF) || hasPermission(Permission.ASSIGN_PERMISSIONS);
      break;
    case 'system':
      hasAccess = hasPermission(Permission.SYSTEM_SETTINGS);
      break;
    default:
      hasAccess = canViewFinancials();
  }

  if (!hasAccess) {
    if (placeholder) {
      return <>{placeholder}</>;
    }

    return (
      <div className={`inline-flex items-center gap-2 px-3 py-1 bg-gray-100 text-gray-500 rounded-lg text-sm ${className}`}>
        <AlertTriangle className="h-4 w-4" />
        <span>{t('errors.sensitiveDataHidden')}</span>
      </div>
    );
  }

  return <>{children}</>;
};

export default ProtectedComponent;
